import * as vp from 'vopay';
import { FunctionComponent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './BankVerifyHistoryForm.module.scss';

import { Button } from '#shared/components/UI';

const { Box, Select, TextField, DateRangePicker, AVAILABLE_TIMEZONES } = vp;

type BankVerifySearchParams = {
  requestStatus: string;
  verificationStatus: string;
  searchTerm: string;
  timeRange: [vp.Timestamp, vp.Timestamp];
  timeZone: string;
};

type SearchFormProps = {
  requestStatus: string;
  timeZone: string;
  timeRange?: [vp.Timestamp, vp.Timestamp];
  verificationStatus: string;
  searchTerm: string;
  initialTimeRange: [vp.Timestamp, vp.Timestamp];
  hasInvalidEndDate?: boolean;
  shouldShowEndDateError?: boolean;
  setShouldShowEndDateError?: (event: boolean) => void;
  search: (searchParams: BankVerifySearchParams) => void;
};

type FC = FunctionComponent<SearchFormProps>;

const BankVerifyHistoryForm: FC = (props: SearchFormProps) => {
  const {
    requestStatus,
    timeZone,
    verificationStatus,
    searchTerm,
    initialTimeRange,
    hasInvalidEndDate,
    shouldShowEndDateError,
    setShouldShowEndDateError,
    search,
  } = props;

  const [currentRequestStatus, setCurrentRequestStatus] = useState<string>(requestStatus);
  const [currentVerificationStatus, setCurrentVerificationStatus] = useState<string>(verificationStatus);
  const [currentSearchTerm, setCurrentSearchTerm] = useState<string>(searchTerm);
  const [timeRange, setTimeRange] = useState<[vp.Timestamp, vp.Timestamp]>(initialTimeRange);
  const [currentTimeZone, setCurrentTimeZone] = useState<string>(timeZone);
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.BankVerifyHistory.SearchForm' });

  const requestStatusOptions = [
    { value: 'all', label: t('Options.RequestStatusOptions.all', { defaultValue: 'All' }) },
    { value: 'active', label: t('Options.RequestStatusOptions.active', { defaultValue: 'Active' }) },
    { value: 'expired', label: t('Options.RequestStatusOptions.expired', { defaultValue: 'Expired' }) },
  ];

  const verificationStatusOptions = [
    { value: 'all', label: t('Options.VerificationStatusOptions.all', { defaultValue: 'All' }) },
    { value: 'verified', label: t('Options.VerificationStatusOptions.verified', { defaultValue: 'Verified' }) },
    { value: 'unverified', label: t('Options.VerificationStatusOptions.unverified', { defaultValue: 'Unverified' }) },
    { value: 'failed', label: t('Options.VerificationStatusOptions.failed', { defaultValue: 'Failed' }) },
  ];

  const timezoneName = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const initialTimeZones = AVAILABLE_TIMEZONES.map((tz) => ({
    label: tz,
    value: tz,
  })).filter((tz) => !tz.value.toLowerCase().includes('utc') && !tz.value.includes(timezoneName));

  const timeZoneOptions = [
    {
      label: 'UTC',
      value: 'UTC',
    },
    {
      label: timezoneName,
      value: timezoneName,
    },
    ...initialTimeZones,
  ];

  return (
    <Box flx="row" gap="sm" wrap="wrap">
      <div className={styles.formContainer}>
        <DateRangePicker
          onChange={(range) => {
            setTimeRange(range);
          }}
          initialTime={initialTimeRange}
          triggerVariant="outlineShadow"
          hasInvalidEndDate={hasInvalidEndDate}
          shouldShowEndDateError={shouldShowEndDateError}
          setShouldShowEndDateError={setShouldShowEndDateError}
        />

        <Select
          version="2"
          placeholder={t('requestStatus', { defaultValue: 'Request Status' })}
          options={requestStatusOptions}
          name="status"
          value={currentRequestStatus}
          onBlur={() => {}}
          onChange={(e) => {
            setCurrentRequestStatus(e.target.value);
          }}
        />

        <Select
          version="2"
          placeholder={t('verificationStatus', { defaultValue: 'Verification Status' })}
          options={verificationStatusOptions}
          name="verificationStatus"
          value={currentVerificationStatus}
          onBlur={() => {}}
          onChange={(e) => {
            setCurrentVerificationStatus(e.target.value);
          }}
        />

        <Select
          version="2"
          placeholder={t('timezone', { defaultValue: 'Timezone' })}
          options={timeZoneOptions}
          name="timeZone"
          value={currentTimeZone}
          onBlur={() => {}}
          onChange={(e) => {
            setCurrentTimeZone(e.target.value);
          }}
        />

        <TextField
          key="AccountSearchKeyword"
          name="AccountSearchKeyword"
          placeholder={t('keyword', { defaultValue: 'Keyword' })}
          value={currentSearchTerm}
          onBlur={() => {}}
          onChange={(e) => {
            setCurrentSearchTerm(e.target.value);
          }}
          variant="outlineShadow"
        />

        <Button
          id="search-verify"
          size="small"
          colorScheme="primary"
          onClick={() => {
            search({
              requestStatus: currentRequestStatus,
              verificationStatus: currentVerificationStatus,
              searchTerm: currentSearchTerm,
              timeRange,
              timeZone: currentTimeZone,
            });
          }}
        >
          {t('search', { defaultValue: 'Search' })}
        </Button>
      </div>
    </Box>
  );
};

export default BankVerifyHistoryForm;
